import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import keyBy from 'lodash/keyBy';
import some from 'lodash/some';
import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
import { useDispatch, useSelector } from 'react-redux';
import {
  PATIENT_CREATE_PATIENT,
  PROJECT_ATTACH_PARTICIPATION,
  PATIENT_ATTACH_PARTICIPATION,
  PROJECT_PROFILE_CREATE_PARTICIPATION,
  PROJECT_EXPORT_PARTICIPANTS,
} from '../../../../common/permissions';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import {
  apiZedocOneProject,
  apiZedocSearchPatientRecords,
} from '../../../../common/api/zedoc';
import PatientRecord from '../../../../common/selectors/PatientRecord';
import Project from '../../../../common/selectors/Project';
import Dropdown from '../../../../components/Dropdown';
import Button from '../../../../components/Button';
import PageBar from '../../../../components/Layout/PageBar';
import Table from '../../../../components/lists/Table';
import branding from '../../../../utils/branding';
import useProjectVariables from '../../../../utils/useProjectVariables';
import useDocumentTitle from '../../../../utils/useDocumentTitle';
import usePermission, { useRealm } from '../../../../utils/usePermission';
import usePagination from '../../../../utils/usePagination';
import { notifyError, notifySuccess } from '../../../../utils/notify';
import ConnectedFilters, {
  isQuickSearchPreset,
  useParticipationsPresets,
} from './ConnectedFilters';
import { openProjectProfileDialog } from '../../actions';
import { getFilters } from '../../selectors';
import cleanFilters from '../../../../utils/cleanFilters';
import flattenFilters from '../../../../utils/flattenFilters';
import HighlightMatchedProperty from '../../../../components/HighlightMatchedProperty';
import useDownload from '../../../../utils/useDownload';
// import MatchedProperties from '../../../../components/MatchedProperties';

const ProjectParticipations = ({ projectId }) => {
  const { t, i18n } = useTranslation();
  const canDownloadCSV = usePermission([PROJECT_EXPORT_PARTICIPANTS]);
  const dispatch = useDispatch();
  const { variables, variablesReady } = useProjectVariables(projectId);
  const project = useSelector(Project.one().whereIdEquals(projectId));
  const rawFilters = useSelector(getFilters);
  const filters = useMemo(
    () => cleanFilters(rawFilters, { anyOf: true }),
    [rawFilters],
  );
  // The store starts with no filters, and ConnectedFilters' quick-search boxes
  // are written into it by an effect in components/Filters/index.jsx, one
  // render later. Subscribing before that fires an UNFILTERED
  // searchPatientRecords over the whole project, immediately followed by the
  // real one - two heavy queries for one page view, even when the search boxes
  // are empty. Hold the subscription until those filters have been written -
  // but only ONCE: the "Clear" button empties them again, and that must still
  // search.
  //
  // The quick-search boxes are built ONLY from the name presets of this
  // screen (ConnectedFilters). A project that yields none never gets anything
  // written, so waiting for a non-empty store would keep the list closed - and
  // spinning - for good. So the gate also opens once the project and its
  // variables have loaded and the SAME preset list ConnectedFilters uses has no
  // quick-search preset: there is nothing to wait for. The presets are read
  // through the shared hook rather than re-derived from the variables, so a
  // name variable that produces no preset (outside the allowed scopes, or a
  // schema that is not filterable) cannot leave the gate closed.
  // useProjectFilters returns [] until BOTH its project and its variables
  // subscriptions are ready, so an empty list only means "no presets" once
  // those two are. The same two subscriptions are read here (same params, so
  // they are shared, and report ready in the same render as the hook's) -
  // checking the project document in the store instead would open the gate
  // early whenever the project was already cached from another screen.
  const presets = useParticipationsPresets(projectId);
  const { ready: projectReady } = useDDPSubscription(
    projectId &&
      apiZedocOneProject.withParams({
        projectId,
      }),
  );
  const hasQuickSearchPreset = useMemo(
    () => some(presets, isQuickSearchPreset),
    [presets],
  );
  const seededRef = useRef(false);
  if (
    !isEmpty(rawFilters) ||
    (variablesReady && projectReady && !hasQuickSearchPreset)
  ) {
    seededRef.current = true;
  }
  const filtersReady = seededRef.current;

  const {
    ready: subscriptionsReady,
    items: patients,
    getPaginationProps,
    paginationKey,
  } = usePagination({
    debounceMs: 500,
    selector: PatientRecord,
    getSubscription: (currentPage, resultsPerPage) =>
      // NOTE: must be null (not false) when gated - useDDPSubscription accepts
      // only nil or a plain object.
      filtersReady
        ? apiZedocSearchPatientRecords.withParams({
            projectId,
            filters,
            controlId: '$meta.id',
            pageIndex: currentPage - 1,
            resultsPerPage,
            sorter: {
              type: 'property',
              order: 'Descending',
              settings: {
                id: 'createdAt',
              },
            },
          })
        : null,
  });
  const columns = useMemo(() => {
    const arr = [];
    const filtersById = keyBy(flattenFilters(rawFilters), 'id');
    if (variables) {
      forEach(variables, (variable) => {
        const isNameColumn = variable.isPatientName();

        if (
          variable.isAtomic() &&
          (variable.isParticipation() || variable.isPatient())
        ) {
          arr.push({
            title: variable.name,
            dataIndex: variable._id,
            key: variable._id,
            render: (text, record) => {
              const isNhi = variable.name === 'National health identifier';
              const displayValue = isNhi
                ? '*******'
                : variable.getDisplayValue(record.getVariable(variable._id), {
                    projectId,
                    language: i18n.language,
                    useDateTimeFormat: true,
                  });
              return (
                <div
                  className={`flex items-center min-h-20 ${
                    isNameColumn ? 'w-52' : ''
                  }`}
                >
                  <HighlightMatchedProperty
                    displayValue={displayValue}
                    propertyKey={`variables.${variable._id}`}
                    filtersById={filtersById}
                    allMatchedProperties={
                      record[paginationKey] && record[paginationKey].filters
                    }
                  />
                </div>
              );
            },
          });
        }
      });
    }

    return arr;
  }, [variables, projectId, i18n, paginationKey, rawFilters]);

  const title = t('recipientsInProject', { context: branding });
  const projectName = project?.getName();
  const subTitle = projectName;
  useDocumentTitle([title, subTitle]);

  const createRealm = useRealm(
    [
      PATIENT_CREATE_PATIENT,
      PATIENT_ATTACH_PARTICIPATION,
      PROJECT_ATTACH_PARTICIPATION,
      PROJECT_PROFILE_CREATE_PARTICIPATION,
    ],
    {
      scope: project ? project.getDomains() : null,
    },
  );

  const handleOnAddToProject = () => dispatch(openProjectProfileDialog());
  const download = useDownload();

  const handleOnProjectExport = useCallback(() => {
    if (!project) {
      notifyError()('Please select a project first');
    } else {
      download(`/api/v1/csvParticipations/projects/${projectId}`);
      setTimeout(() => {
        notifySuccess(t('confirmations:downloadResponsesCSV.success'))();
      }, 1000);
    }
  }, [download, projectId, project, t]);

  return (
    <div className="stack-6">
      <PageBar
        title={title}
        subTitle={subTitle}
        backUrl="/patients-in-project"
      />
      <div className="bg-surface py-4 shadow rounded-md stack-4">
        <div className="px-4 stack-4">
          <ConnectedFilters projectId={projectId} />
          <div className="cluster-4 items-center justify-between">
            <span className="text-lg font-medium">{projectName}</span>
            <div className="cluster-2">
              <Button
                data-testid="button-add-patient"
                onClick={handleOnAddToProject}
                disabled={isEmpty(createRealm)}
              >
                {t('addToProject')}
              </Button>
              {canDownloadCSV && (
                <Dropdown
                  data-testid="submenu"
                  icon={<DotsHorizontalIcon />}
                  items={[
                    {
                      testId: 'submenu-item-download',
                      onClick: handleOnProjectExport,
                      label: t('downloadCSV'),
                    },
                  ]}
                />
              )}
            </div>
          </div>
        </div>
        <Table
          data-testid="projects-table"
          rowKey="_id"
          dataSource={patients}
          columns={columns}
          // A gated (null) subscription reports ready, so the gate is checked
          // too - otherwise the table shows "No data" before the first query.
          loading={!filtersReady || !subscriptionsReady}
          pagination={getPaginationProps()}
        />
      </div>
    </div>
  );
};

ProjectParticipations.propTypes = {
  projectId: PropTypes.string.isRequired,
};

export default ProjectParticipations;
